/**
 * "Resend for approval" — one control, two mount points.
 *
 * `POST /companies/:id/approval-request` re-raises the add-a-school approval and
 * emails the approvers group again. It is offered on `CompanyDetailPage` (beside
 * the approval state, in Account details) and as a row action on `CompaniesPage`,
 * in both the desktop table and the sub-`md` card list.
 *
 * ## Why one component rather than a button on each page
 *
 * Everything difficult here is *state per record*, not layout: an in-flight
 * guard, an abort on unmount, a cooldown countdown, and an outcome sentence that
 * must be worded from counts rather than from the status code. Two copies would
 * agree the day they were written and drift on the first correction — and what
 * would drift is a sentence about whether a customer-facing email went out.
 * `@/lib/company-approval` owns the wording and the routing; this owns the state.
 *
 * ## ⚠️ Pressing it twice mails the whole approvers group twice
 *
 * A row button is one click per row, and people click again when nothing visibly
 * happens. So:
 *
 *  - **A second request for the same record is structurally impossible while one
 *    is in flight** — the button is `loading`, which `Button` itself turns into
 *    `disabled`, and `sending` is checked again inside the handler. State is
 *    per-mounted-control, so one row being busy never disables another.
 *  - **Feedback is immediate and lives beside the button that was pressed**, not
 *    in a page-level banner a reader scrolling a table would never see.
 *  - **A `429` is expected, not exceptional.** The cooldown cannot be pre-empted
 *    — no `lastRequestedAt` reaches this app on a company read — so a
 *    just-resent company still looks resendable and the refusal is where anyone
 *    finds out. That is accepted: the button enables on any pending company and
 *    the 429 disables it with a live countdown.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ApiError, api } from '@/lib/api';
import {
  type ResendCooldown,
  type ResendOutcome,
  type ResendRefusal,
  cooldownNote,
  describeResendOutcome,
  notPendingReason,
  resendDenialFor,
  resendRefusalOf,
} from '@/lib/company-approval';
import { cx } from '@/lib/cx';
import { useAuth } from '@/lib/use-auth';
import type { CompanyApprovalStatus } from '@/lib/types';

/** Everything this control needs off a company — satisfied by a list row and by
 *  the full record alike, so neither page fetches anything extra. */
export interface ResendApprovalCompany {
  id: number;
  name: string;
  approvalStatus: CompanyApprovalStatus;
}

/**
 * How the control renders. The BEHAVIOUR is identical in all three; only the
 * affordance differs, because the three surfaces have different room and
 * different conventions.
 *
 * - `detail` — a labelled button with the outcome underneath. The detail page has
 *   the room, and it is the surface where a refusal must be readable in full.
 * - `row` — an icon-only ghost button, matching every other action in the pinned
 *   Actions column, with the outcome as a `title` and a live region off-screen.
 * - `card` — a labelled small button, matching the sub-`md` card list.
 */
type Variant = 'detail' | 'row' | 'card';

interface ResendState {
  sending: boolean;
  outcome: ResendOutcome | null;
  refusal: ResendRefusal | null;
  /** A failure that is not one of the three known refusals — network, 403, or a
   *  code newer than our map. Always the server's own sentence. */
  error: string | null;
  /** Seconds left on a 429's cooldown, counted down locally. */
  waitSeconds: number | null;
}

const IDLE: ResendState = {
  sending: false,
  outcome: null,
  refusal: null,
  error: null,
  waitSeconds: null,
};

const TONE_TEXT: Record<ResendOutcome['tone'], string> = {
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

function SendIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M3 10 17 3l-4 14-3-6-7-1Z" />
    </svg>
  );
}

/**
 * The approval state as a badge — `pending` is a NORMAL state, not a fault.
 *
 * A school waiting on a decision is waiting on a person, so it reads as
 * information rather than as something that went wrong. `rejected` is the only
 * one coloured as a problem, and even then the label carries the meaning: colour
 * is never the only signal.
 */
export function ApprovalStatusBadge({ status }: { status: CompanyApprovalStatus }) {
  if (status === 'approved') {
    return (
      <Badge tone="success" dot>
        Approved
      </Badge>
    );
  }
  if (status === 'rejected') {
    return (
      <Badge tone="danger" dot>
        Rejected
      </Badge>
    );
  }
  return (
    <Badge tone="info" dot>
      Awaiting approval
    </Badge>
  );
}

export function ResendApprovalControl({
  company,
  variant,
  className,
}: {
  company: ResendApprovalCompany;
  variant: Variant;
  className?: string;
}) {
  const { ability } = useAuth();
  const [state, setState] = useState<ResendState>(IDLE);

  /*
   * The in-flight controller, so an unmount aborts the request rather than
   * leaving it to resolve into a component that is gone. `sending` in state
   * drives the UI; this ref is what the effect can reach on teardown.
   */
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  /*
   * Reset when the row changes identity or the record settles.
   *
   * Without the `approvalStatus` half, a "queued to 2 approvers" line would sit
   * under a company that has since been approved — a true sentence about a past
   * event, rendered as though it were the current state.
   *
   * ⚠️ **Adjusted DURING RENDER, not in an effect**, which is React's own
   * documented pattern for resetting state when a prop changes: React discards
   * the in-progress output and re-renders immediately, so nothing is committed
   * and no second paint happens. An effect here would paint the stale outcome
   * for a frame first, and on a table of rows recycling as pages change that
   * frame is visible — the previous company's result under the new company's
   * name. The row is not keyed from outside because this control is mounted at
   * three call sites and a key at each is three chances to forget one.
   */
  const identity = `${company.id}:${company.approvalStatus}`;
  const [renderedFor, setRenderedFor] = useState(identity);
  if (renderedFor !== identity) {
    setRenderedFor(identity);
    setState(IDLE);
  }

  /*
   * The 429's countdown: one timer, replaced on every tick, cleared on unmount.
   *
   * It stops AT zero rather than clamping back to `null` afterwards — the guard
   * below is `> 0`, so zero already reads as "the wait is over" and re-enables
   * the button. Nulling it would be a second state change saying the same thing,
   * and `waitFrom` never produces a non-positive value, so a countdown that
   * starts at all has somewhere to run.
   */
  useEffect(() => {
    if (state.waitSeconds === null || state.waitSeconds <= 0) return;
    const timer = window.setTimeout(() => {
      setState((prev) =>
        prev.waitSeconds === null
          ? prev
          : { ...prev, waitSeconds: Math.max(0, prev.waitSeconds - 1) },
      );
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [state.waitSeconds]);

  const denial = resendDenialFor(ability);
  const notPending = notPendingReason(company.approvalStatus);

  const send = useCallback(async () => {
    // Checked here as well as expressed through `loading`: the button being
    // disabled is the optimisation, this is the guarantee. Each press mails the
    // whole approvers group, so a double-fire is a real cost, not a wasted call.
    if (state.sending) return;

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    setState({ ...IDLE, sending: true });

    try {
      const response = await api.resendCompanyApproval(company.id, controller.signal);
      if (controller.signal.aborted) return;
      setState({ ...IDLE, outcome: describeResendOutcome(response.data) });
    } catch (caught) {
      if (controller.signal.aborted) return;

      if (caught instanceof ApiError) {
        /*
         * A 403 is handled even though the control is gated: a role can be taken
         * away under an access token that lasts over five days, so the gate is
         * the optimisation and this is the guarantee. A 403 carries no
         * `details`, so it is a plain sentence, never a field error.
         */
        if (caught.status === 403) {
          setState({
            ...IDLE,
            error:
              resendDenialFor(ability) ??
              'Your account no longer has permission to send approval requests.',
          });
          return;
        }

        const refusal = resendRefusalOf(caught);
        if (refusal) {
          setState({
            ...IDLE,
            refusal,
            waitSeconds: waitFrom(refusal.cooldown),
          });
          return;
        }
      }

      // Unrecognised: the server's own sentence, never a classification we
      // invented. An unknown code means "newer than this branch", not "not a
      // refusal".
      setState({
        ...IDLE,
        error:
          caught instanceof Error
            ? caught.message
            : 'The approval request could not be sent. Try again.',
      });
    } finally {
      /*
       * A backstop, and deliberately a no-op today: every branch above settles
       * `sending` as part of the state it writes, and every early return happens
       * only when the signal is aborted, which this guard then skips. It is here
       * so that a branch added later cannot leave the button spinning for ever —
       * the failure that state would produce is a row nobody can resend, with
       * nothing logged and nothing to retry.
       */
      if (!controller.signal.aborted) {
        setState((prev) => (prev.sending ? { ...prev, sending: false } : prev));
      }
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [ability, company.id, state.sending]);

  const waiting = state.waitSeconds !== null && state.waitSeconds > 0;
  const terminal = state.refusal?.terminal === true;

  /*
   * Why each disabled state exists, in the order they are tested. A bare
   * `disabled` makes a permission denial, a settled approval and an ordinary
   * cooldown identical, so every one of these carries its own sentence.
   */
  const disabledReason = denial ?? notPending ?? (terminal ? state.refusal?.message : null) ?? null;
  const disabled = disabledReason !== null || waiting;

  const label = 'Resend for approval';
  const accessibleLabel = `${label} for ${company.name}`;

  const message = messageFor(state, waiting);

  if (variant === 'row') {
    /*
     * Icon-only, matching the other actions in the pinned column. The outcome
     * cannot render as prose in a table cell, so it goes on the `title` — where
     * the no-prose-in-a-cell rule already puts explanatory text — and into an
     * off-screen live region so it is announced rather than only hoverable.
     */
    const title = disabledReason ?? message?.text ?? label;
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          loading={state.sending}
          disabled={disabled}
          leadingIcon={<SendIcon />}
          aria-label={accessibleLabel}
          title={title}
          onClick={() => void send()}
        />
        <span role="status" className="sr-only">
          {message?.text ?? ''}
        </span>
      </>
    );
  }

  const buttonSize = variant === 'card' ? 'sm' : 'md';

  return (
    <div className={cx('grid gap-1.5', className)}>
      <div>
        <Button
          variant="secondary"
          size={buttonSize}
          loading={state.sending}
          disabled={disabled}
          leadingIcon={<SendIcon />}
          aria-label={accessibleLabel}
          onClick={() => void send()}
        >
          {waiting ? `Resend in ${state.waitSeconds}s` : label}
        </Button>
      </div>

      {/*
        One live region for every outcome, so a send never reports itself twice
        or in two different voices. `role="alert"` only where something needs
        acting on; an ordinary success is polite.
      */}
      {(message || disabledReason) && (
        <p
          role={message?.tone === 'success' || message === null ? 'status' : 'alert'}
          className={cx(
            'm-0 text-xs [overflow-wrap:anywhere]',
            message ? TONE_TEXT[message.tone] : 'text-fg-muted',
          )}
        >
          {message?.text ?? disabledReason}
        </p>
      )}

      {message?.notes.map((note) => (
        <p key={note} className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
          {note}
        </p>
      ))}
    </div>
  );
}

/* --- helpers ------------------------------------------------------------- */

/**
 * Seconds to count down, preferring the server's own figure.
 *
 * `nextAllowedAt` is the fallback rather than the primary: deriving a duration
 * from a timestamp measures the gap between the server's clock and the browser's
 * as well as the cooldown, and a skewed clock would show a countdown that ends
 * before the server agrees. `retryAfterSeconds` is a duration on the wire and
 * carries no such assumption.
 *
 * `null` — neither field usable, or the window has already passed — means no
 * countdown, so the caller renders the refusal without one rather than inventing
 * a number.
 */
function waitFrom(cooldown: ResendCooldown | null): number | null {
  if (!cooldown) return null;
  if (cooldown.retryAfterSeconds !== null) return cooldown.retryAfterSeconds;
  if (cooldown.nextAllowedAt === null) return null;
  const remaining = Math.ceil((Date.parse(cooldown.nextAllowedAt) - Date.now()) / 1000);
  return Number.isFinite(remaining) && remaining > 0 ? remaining : null;
}

interface Message {
  tone: ResendOutcome['tone'];
  text: string;
  notes: string[];
}

function messageFor(state: ResendState, waiting: boolean): Message | null {
  if (state.outcome) {
    return {
      tone: state.outcome.tone,
      text: state.outcome.headline,
      notes: state.outcome.notes,
    };
  }

  if (state.refusal) {
    const note = cooldownNote(state.refusal.cooldown);
    return {
      // A cooldown is not a failure — it clears on its own — so it is a warning
      // where the two terminal refusals are dangers.
      tone: state.refusal.kind === 'too-recently' ? 'warning' : 'danger',
      // The server's own sentence, rendered as written. It is the only text that
      // names the real state.
      text: state.refusal.message,
      notes: [
        ...(note ? [note] : []),
        ...(waiting ? [] : state.refusal.kind === 'too-recently' ? ['You can send it again now.'] : []),
      ],
    };
  }

  if (state.error) return { tone: 'danger', text: state.error, notes: [] };
  return null;
}
